import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface VoiceWaveformProps {
  isActive: boolean;
  className?: string;
  barCount?: number;
  color?: "accent" | "primary" | "danger";
}

export function VoiceWaveform({
  isActive,
  className,
  barCount = 5,
  color = "accent",
}: VoiceWaveformProps) {
  const [bars, setBars] = useState<number[]>(Array(barCount).fill(0.3));
  const animationRef = useRef<number>();
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);

  const colorMap = {
    accent: "bg-accent",
    primary: "bg-primary",
    danger: "bg-danger",
  };

  useEffect(() => {
    if (isActive) {
      // Simulate waveform for visual feedback without opening a second stream
      // In a real app, you'd pass the existing MediaStream from useVoiceRecorder
      const animate = () => {
        if (!isActive) return;
        
        setBars(prev =>
          prev.map(() => 0.2 + Math.random() * 0.6)
        );
        
        animationRef.current = requestAnimationFrame(() => {
          setTimeout(animate, 100);
        });
      };
      animate();
    } else {
      // Animate bars down when stopping
      setBars(Array(barCount).fill(0.15));
      
      // Cleanup
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [isActive, barCount]);

  return (
    <div
      className={cn(
        "flex items-center justify-center gap-1 h-6",
        className
      )}
    >
      {bars.map((height, index) => (
        <div
          key={index}
          className={cn(
            "w-1 rounded-full transition-all duration-75",
            colorMap[color],
            isActive ? "opacity-100" : "opacity-40"
          )}
          style={{
            height: `${height * 100}%`,
            minHeight: "4px",
            transitionDelay: `${index * 20}ms`,
          }}
        />
      ))}
    </div>
  );
}

// Circular waveform variant for larger displays
interface CircularWaveformProps {
  isActive: boolean;
  size?: number;
  className?: string;
}

export function CircularWaveform({
  isActive,
  size = 80,
  className,
}: CircularWaveformProps) {
  const [scale, setScale] = useState(1);

  useEffect(() => {
    if (!isActive) {
      setScale(1);
      return;
    }

    const animate = () => {
      setScale(0.9 + Math.random() * 0.2);
    };

    const interval = setInterval(animate, 150);
    return () => clearInterval(interval);
  }, [isActive]);

  return (
    <div
      className={cn(
        "relative flex items-center justify-center",
        className
      )}
      style={{ width: size, height: size }}
    >
      {/* Outer glow ring */}
      <div
        className={cn(
          "absolute inset-0 rounded-full transition-all duration-300",
          isActive
            ? "bg-accent/20 animate-pulse"
            : "bg-muted/50"
        )}
        style={{
          transform: `scale(${isActive ? scale * 1.3 : 1})`,
        }}
      />
      
      {/* Middle ring */}
      <div
        className={cn(
          "absolute rounded-full transition-all duration-200",
          isActive ? "bg-accent/30" : "bg-muted/30"
        )}
        style={{
          width: size * 0.75,
          height: size * 0.75,
          transform: `scale(${isActive ? scale * 1.15 : 1})`,
        }}
      />
      
      {/* Inner circle */}
      <div
        className={cn(
          "absolute rounded-full transition-all duration-150",
          isActive
            ? "bg-accent shadow-[0_0_20px_4px_hsl(var(--accent)/0.4)]"
            : "bg-muted"
        )}
        style={{
          width: size * 0.5,
          height: size * 0.5,
          transform: `scale(${isActive ? scale : 1})`,
        }}
      />
    </div>
  );
}
